import { Component, DestroyRef, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounce, EMPTY, fromEvent, merge, startWith, switchMap, timer } from 'rxjs';
import { distinctUntilChanged, filter, throttleTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CORE_AUTH_PAGE_PATH, CORE_DEBUG, CORE_MAIN_PAGE_PATH } from '../core.tokens';
import { IRestaurantTable } from './models';
import { AuthService, InteractionService, RestaurantTableService } from './services';
import { DebugComponent } from './components';

@Component({
  selector: 'core-root',
  templateUrl: './root.component.html',
  imports: [
    RouterOutlet,
    DebugComponent,
  ]
})
export class RootComponent {

  public readonly debug = inject(CORE_DEBUG);
  private readonly mainPagePath = inject(CORE_MAIN_PAGE_PATH);
  private readonly authPagePath = inject(CORE_AUTH_PAGE_PATH);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly interaction = inject(InteractionService);
  private readonly auth = inject(AuthService);
  private readonly restaurantTable = inject(RestaurantTableService);

  public readonly authorization = toSignal(this.auth.authorization$, {
    initialValue: true,
  });

  constructor() {
    this.translate.addLangs(['en-CA', 'fr-CA', 'ar-AE']);
    this.translate.setFallbackLang('en-CA');

    this.translate.onLangChange.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((event) => {
      document.documentElement.lang = event.lang;
      document.documentElement.dir = event.lang.startsWith('ar') ? 'rtl' : 'ltr';
    });

    this.auth.logined$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      void this.router.navigate(['/', this.mainPagePath]);
    });

    this.auth.logouted$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      void this.router.navigate(['/', this.authPagePath]);
    });

    this.restaurantTable.table$.pipe(
      filter((table): table is IRestaurantTable => table !== null),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((table) => {
      // Set language for restaurant
      this.translate.setFallbackLang(table ? table.language : 'en-CA');
    });

    merge(
      this.auth.logined$,
      fromEvent(document, 'click', { passive: true }),
      fromEvent(document, 'scroll', { passive: true }),
      fromEvent(document, 'touchstart', { passive: true }),
    ).pipe(
      throttleTime(100),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.interaction.interact();
    });

    this.interaction.interactions$.pipe(
      startWith('initial'),
      switchMap(() => this.interaction.idleDelay$),
      debounce((seconds) => {
        if (seconds <= 0) {
          return EMPTY;
        }

        return timer(seconds * 1000);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.interaction.idle();
    });
  }

}
